export type Kamae =
  | "jodan"    // 上段 — mãos acima da cabeça
  | "chudan"   // 中段 — mãos ao nível do peito, ponta à frente
  | "gedan"    // 下段 — ponta apontada para baixo/frente
  | "hasso"    // 八相 — lâmina vertical ao lado direito da cabeça
  | "waki"     // 脇構え — lâmina atrás do corpo
  | "transit"; // em movimento entre kamae

export interface KataFrame {
  t: number;
  tip:  [number, number, number];
  grip: [number, number, number];
  label?: string;
  kamae?: Kamae;
  postureDesc?: string; // instrução de postura para exibir no painel
}

export interface KataDefinition {
  id: number;
  name: string;
  nameJP: string;
  description: string;
  waza: string;
  frames: KataFrame[];
}

export const KATAS: KataDefinition[] = [
  {
    id: 1,
    name: "Ippon-me",
    nameJP: "一本目",
    description: "The first cut — vertical shomen, the foundation of kenjutsu.",
    waza: "Shomen-giri ↓",
    frames: [
      {
        t: 0.0, tip: [0, 2.1, -0.8], grip: [0, 1.8, -0.6],
        label: "Jōdan no kamae",
        kamae: "jodan",
        postureDesc: "Right foot forward, knees slightly bent.\nHands above the head, elbows open.\nEyes fixed on the opponent.",
      },
      {
        t: 0.8, tip: [0, 1.6, -0.7], grip: [0, 1.5, -0.6],
        kamae: "transit",
        postureDesc: "Brace the core. Let weight drop through the hips.\nDo not force with the arms — let gravity lead.",
      },
      {
        t: 1.4, tip: [0, 0.9, -0.7], grip: [0, 1.1, -0.6],
        label: "Shomen-giri ↓",
        kamae: "gedan",
        postureDesc: "Tenouchi: tighten the grip at impact.\nLegs absorb the strike; knees bend slightly.\nStop the blade on the floor line, not beyond it.",
      },
      {
        t: 2.2, tip: [0, 0.9, -0.7], grip: [0, 1.1, -0.6],
        label: "Zanshin — Awareness",
        kamae: "gedan",
        postureDesc: "Hold alert posture (zanshin).\nDo not relax the shoulders — attention remains present.\nBreathe calmly through the nose.",
      },
    ],
  },
  {
    id: 2,
    name: "Nihon-me",
    nameJP: "二本目",
    description: "Bilateral yoko-giri — hip rotation, two horizontal cuts.",
    waza: "Yoko-giri → ←",
    frames: [
      {
        t: 0.0, tip: [-0.55, 1.4, -0.7], grip: [-0.15, 1.4, -0.6],
        label: "Chudan left",
        kamae: "chudan",
        postureDesc: "Feet in hanmi (diagonal), left shoulder slightly forward.\nBlade horizontal at the opponent's shoulder level.\nHips open for the rightward cut.",
      },
      {
        t: 0.7, tip: [0.55, 1.4, -0.7], grip: [0.15, 1.4, -0.6],
        label: "Yoko-giri →",
        kamae: "transit",
        postureDesc: "Rotation starts from the hips, not the shoulders.\nArms follow the torso.\nTenouchi at full extension.",
      },
      {
        t: 1.6, tip: [0.55, 1.4, -0.7], grip: [0.15, 1.4, -0.6],
        kamae: "chudan",
        postureDesc: "Rebalance. Shift weight to the right foot.\nPrepare the reverse rotation.",
      },
      {
        t: 2.3, tip: [-0.55, 1.4, -0.7], grip: [-0.15, 1.4, -0.6],
        label: "Yoko-giri ←",
        kamae: "transit",
        postureDesc: "Reverse rotation — pull with the left elbow.\nThe body turns as one unit, not just the arms.",
      },
      {
        t: 3.0, tip: [-0.55, 1.4, -0.7], grip: [-0.15, 1.4, -0.6],
        label: "Zanshin — Awareness",
        kamae: "chudan",
        postureDesc: "Stable posture, blade aligned with the opponent.\nEyes forward, controlled breathing.",
      },
    ],
  },
  {
    // Content unchanged (cardinal-4) — id/name swapped with the kesagiri
    // kata below. Tags as `seq: 3` in phase III's beatmap (dojo-03.json),
    // unlocking 3rd — before kesa (phase IV) is taught, which matches this
    // kata's content (cardinal-only, nothing diagonal). The old numbering
    // had this kata as "4th" unlocking BEFORE a kata labeled "3rd" (phase
    // III comes before phase IV), which broke SkillTreeScreen's linear
    // prevUnlocked gate (see the campaign v2 doc's didactic-progression
    // audit).
    id: 3,
    name: "Sanbon-me",
    nameJP: "三本目",
    description: "Partial happo-giri — four cuts covering the cardinal axes.",
    waza: "Happo-giri (4 dir.)",
    frames: [
      {
        t: 0.0, tip: [0, 2.1, -0.8], grip: [0, 1.8, -0.6],
        label: "Jōdan no kamae",
        kamae: "jodan",
        postureDesc: "Maximum readiness stance.\nBoth hands above the head, elbows aligned.",
      },
      {
        t: 0.6, tip: [0, 0.9, -0.7], grip: [0, 1.1, -0.6],
        label: "Shomen ↓",
        kamae: "gedan",
        postureDesc: "First cut: central vertical.\nHips drop with the blade.",
      },
      {
        t: 1.3, tip: [-0.5, 1.4, -0.7], grip: [-0.1, 1.4, -0.6],
        kamae: "transit",
        postureDesc: "Turn into the horizontal cut.\nPivot on the front foot; do not lift the heels.",
      },
      {
        t: 1.9, tip: [0.5, 1.4, -0.7], grip: [0.1, 1.4, -0.6],
        label: "Yoko →",
        kamae: "transit",
        postureDesc: "Yoko-giri to the right.\nCore stable; only shoulders and arms rotate.",
      },
      {
        t: 2.6, tip: [0, 2.1, -0.8], grip: [0, 1.8, -0.6],
        kamae: "jodan",
        postureDesc: "Return to jodan. Inhale as you lift.",
      },
      {
        t: 3.2, tip: [0, 0.9, -0.7], grip: [0, 1.1, -0.6],
        label: "Shomen ↓",
        kamae: "gedan",
        postureDesc: "Second shomen — faster. Exhale on the cut.",
      },
      {
        t: 4.0, tip: [0, 0.9, -0.7], grip: [0, 1.1, -0.6],
        label: "Zanshin — Awareness",
        kamae: "gedan",
        postureDesc: "Full stability. Four directions covered.",
      },
    ],
  },
  {
    // Content unchanged (kesagiri) — id/name swapped with the cardinal-4
    // kata above. Tags as `seq: 4` in phase IV's beatmap (dojo-04.json),
    // unlocking 4th — consistent, kesa is phase IV's lesson.
    id: 4,
    name: "Yohon-me",
    nameJP: "四本目",
    description: "Diagonal kesa — a sash-like diagonal cut.",
    waza: "Kesagiri ↘ ↙",
    frames: [
      {
        t: 0.0, tip: [-0.4, 1.9, -0.7], grip: [-0.1, 1.7, -0.6],
        label: "Preparation",
        kamae: "jodan",
        postureDesc: "Blade raised to the left, diagonal starting point.\nWeight on the rear foot. Left shoulder high.",
      },
      {
        t: 0.8, tip: [0.4, 1.0, -0.7], grip: [0.2, 1.2, -0.6],
        label: "Kesa-giri ↘",
        kamae: "transit",
        postureDesc: "Diagonal cut from left shoulder to right hip.\nWeight advances with the cut — short step forward.\nTenouchi on arrival.",
      },
      {
        t: 1.6, tip: [0.4, 1.9, -0.7], grip: [0.1, 1.7, -0.6],
        kamae: "jodan",
        postureDesc: "Return to the right side. Draw the blade in an arc.\nDo not stop midway — keep the motion continuous.",
      },
      {
        t: 2.4, tip: [-0.4, 1.0, -0.7], grip: [-0.2, 1.2, -0.6],
        label: "Kesa-giri ↙",
        kamae: "transit",
        postureDesc: "Diagonal cut from right shoulder to left hip.\nHips lead the motion; arms follow.",
      },
      {
        t: 3.2, tip: [-0.4, 1.0, -0.7], grip: [-0.2, 1.2, -0.6],
        label: "Zanshin — Awareness",
        kamae: "gedan",
        postureDesc: "Finish with stability. Feet parallel, knees bent.\nBlade points toward the fallen opponent.",
      },
    ],
  },
  {
    id: 5,
    name: "Gohon-me",
    nameJP: "五本目",
    description: "Full happo-giri — all eight cuts in continuous sequence.",
    waza: "Happo-giri (8 dir.)",
    frames: [
      {
        t: 0.0, tip: [0, 2.1, -0.8], grip: [0, 1.8, -0.6],
        label: "Jōdan",
        kamae: "jodan",
        postureDesc: "Maximum readiness. Eight imagined opponents around you.\nTake a deep breath before starting.",
      },
      {
        t: 0.5, tip: [0, 0.9, -0.7], grip: [0, 1.1, -0.6],
        label: "Shomen ↓",
        kamae: "gedan",
        postureDesc: "1/8 — central front cut.",
      },
      {
        t: 1.0, tip: [0.5, 1.4, -0.7], grip: [0.1, 1.4, -0.6],
        label: "Yoko →",
        kamae: "transit",
        postureDesc: "2/8 — pivot 90 degrees right. Right foot advances.",
      },
      {
        t: 1.5, tip: [-0.5, 1.4, -0.7], grip: [-0.1, 1.4, -0.6],
        label: "Yoko ←",
        kamae: "transit",
        postureDesc: "3/8 — pivot 180 degrees. Left foot leads.",
      },
      {
        t: 2.0, tip: [0.4, 1.9, -0.7], grip: [0.1, 1.7, -0.6],
        label: "Kesa-age ↗",
        kamae: "transit",
        postureDesc: "4/8 — rising diagonal cut to the right.\nArms rise with the weight of the hips.",
      },
      {
        t: 2.5, tip: [-0.4, 1.9, -0.7], grip: [-0.1, 1.7, -0.6],
        label: "Kesa-age ↖",
        kamae: "transit",
        postureDesc: "5/8 — rising diagonal cut to the left.",
      },
      {
        t: 3.0, tip: [0.4, 1.0, -0.7], grip: [0.2, 1.2, -0.6],
        label: "Kesa ↘",
        kamae: "transit",
        postureDesc: "6/8 — descending kesa to the right.\nWeight drops with the blade.",
      },
      {
        t: 3.5, tip: [-0.4, 1.0, -0.7], grip: [-0.2, 1.2, -0.6],
        label: "Kesa ↙",
        kamae: "transit",
        postureDesc: "7/8 — descending kesa to the left.",
      },
      {
        t: 4.0, tip: [0, 2.1, -0.8], grip: [0, 1.8, -0.6],
        kamae: "jodan",
        postureDesc: "Return to center. The circle closes.",
      },
      {
        t: 4.5, tip: [0, 0.9, -0.7], grip: [0, 1.1, -0.6],
        label: "Shomen final ↓",
        kamae: "gedan",
        postureDesc: "8/8 — final shomen. The strongest cut.\nMaximum tenouchi. Inner kiai.",
      },
      {
        t: 5.2, tip: [0, 0.9, -0.7], grip: [0, 1.1, -0.6],
        label: "Zanshin — Awareness",
        kamae: "gedan",
        postureDesc: "Eight directions mastered.\nThe body is still, but attention remains alert.",
      },
    ],
  },
  {
    id: 6,
    name: "Roppon-me",
    nameJP: "六本目",
    description: "Finale compound kata — fast paired cuts with alternating hands.",
    waza: "Nidan-giri (pares)",
    frames: [
      {
        t: 0.0, tip: [0.4, 1.4, -0.7], grip: [0.1, 1.4, -0.6],
        label: "Chudan right",
        kamae: "chudan",
        postureDesc: "Right foot forward, chudan no kamae.\nTip points toward the opponent's throat.",
      },
      {
        t: 0.5, tip: [-0.4, 1.4, -0.7], grip: [-0.1, 1.4, -0.6],
        label: "Yoko ←",
        kamae: "transit",
        postureDesc: "Leftward horizontal cut — pull with the elbow.",
      },
      {
        t: 1.0, tip: [0, 2.1, -0.8], grip: [0, 1.8, -0.6],
        kamae: "jodan",
        postureDesc: "Lift to jodan without pause. The rhythm is continuous.",
      },
      {
        t: 1.5, tip: [0, 0.9, -0.7], grip: [0, 1.1, -0.6],
        label: "Shomen ↓",
        kamae: "gedan",
        postureDesc: "Central shomen — the most direct cut.",
      },
      {
        t: 2.0, tip: [0.4, 1.9, -0.7], grip: [0.1, 1.7, -0.6],
        kamae: "transit",
        postureDesc: "Rise into the diagonal. Draw the blade in an ascending arc.",
      },
      {
        t: 2.5, tip: [-0.4, 1.0, -0.7], grip: [-0.2, 1.2, -0.6],
        label: "Kesa ↙",
        kamae: "transit",
        postureDesc: "Descending kesa to the left — hips lead.",
      },
      {
        t: 3.0, tip: [-0.4, 1.9, -0.7], grip: [-0.1, 1.7, -0.6],
        kamae: "transit",
        postureDesc: "Left rising arc to prepare the next cut.",
      },
      {
        t: 3.5, tip: [0.4, 1.0, -0.7], grip: [0.2, 1.2, -0.6],
        label: "Kesa ↘",
        kamae: "transit",
        postureDesc: "Descending kesa to the right — the pair is complete.",
      },
      {
        t: 4.2, tip: [0.4, 1.0, -0.7], grip: [0.2, 1.2, -0.6],
        label: "Zanshin — Awareness",
        kamae: "gedan",
        postureDesc: "Firm final posture. The pairs are complete.\nLong breath through the nose.",
      },
    ],
  },
  {
    id: 7,
    name: "Shichihon-me",
    nameJP: "七本目",
    description: "Muso-ken — culminating kata, all waza combined in continuous flow.",
    waza: "Musō-ken (completo)",
    frames: [
      {
        t: 0.0, tip: [0, 2.2, -0.8], grip: [0, 1.9, -0.6],
        label: "Jodan — Apex",
        kamae: "jodan",
        postureDesc: "The highest kamae. Empty mind (mushin).\nAll previous waza are contained in this moment.",
      },
      {
        t: 0.5, tip: [0, 0.9, -0.7], grip: [0, 1.1, -0.6],
        label: "Shomen ↓",
        kamae: "gedan",
        postureDesc: "First cut: full vertical. Inner kiai.",
      },
      {
        t: 1.0, tip: [0.5, 1.4, -0.7], grip: [0.1, 1.4, -0.6],
        label: "Yoko →",
        kamae: "transit",
        postureDesc: "Fluid transition — no pause between cuts.",
      },
      {
        t: 1.4, tip: [-0.4, 1.9, -0.7], grip: [-0.1, 1.7, -0.6],
        label: "Kesa-age ↖",
        kamae: "transit",
        postureDesc: "Rising diagonal left — arms rise with the hips.",
      },
      {
        t: 1.8, tip: [0.4, 1.0, -0.7], grip: [0.2, 1.2, -0.6],
        label: "Kesa ↘",
        kamae: "transit",
        postureDesc: "Immediately descending — the flow does not stop.",
      },
      {
        t: 2.2, tip: [-0.5, 1.4, -0.7], grip: [-0.1, 1.4, -0.6],
        label: "Yoko ←",
        kamae: "transit",
        postureDesc: "Horizontal left. Full hip rotation.",
      },
      {
        t: 2.6, tip: [0.4, 1.9, -0.7], grip: [0.1, 1.7, -0.6],
        label: "Kesa-age ↗",
        kamae: "transit",
        postureDesc: "Rising right — opposite of the previous cut.",
      },
      {
        t: 3.0, tip: [-0.4, 1.0, -0.7], grip: [-0.2, 1.2, -0.6],
        label: "Kesa ↙",
        kamae: "transit",
        postureDesc: "Descending left — the eight-direction cycle closes.",
      },
      {
        t: 3.4, tip: [0, 2.2, -0.8], grip: [0, 1.9, -0.6],
        kamae: "jodan",
        postureDesc: "Return to jodan. One full cycle.\nInhale. Prepare the final strike.",
      },
      {
        t: 3.9, tip: [0, 0.9, -0.7], grip: [0, 1.1, -0.6],
        label: "Shomen final ↓",
        kamae: "gedan",
        postureDesc: "The decisive cut. The whole body as one blade.\nAbsolute tenouchi. The strike that ends the kata.",
      },
      {
        t: 4.8, tip: [0, 0.9, -0.7], grip: [0, 1.1, -0.6],
        label: "Zanshin — Awareness",
        kamae: "gedan",
        postureDesc: "Zanshin — lingering awareness.\nThe kata ends, but attention remains.\nBow slightly: rei.",
      },
    ],
  },
];
